import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FilterControls = ({ 
  filters, 
  onFilterChange, 
  resultCount, 
  onClearFilters,
  isMobile,
  isFilterPanelOpen,
  onToggleFilterPanel 
}) => {
  const serviceTypeOptions = [
    { value: 'all', label: 'All Services' },
    { value: 'house-cleaning', label: 'House Cleaning' },
    { value: 'office-cleaning', label: 'Office Cleaning' },
    { value: 'carpet-cleaning', label: 'Carpet Cleaning' },
    { value: 'deep-cleaning', label: 'Deep Cleaning' },
    { value: 'post-construction', label: 'Post-Construction' },
    { value: 'disinfection', label: 'Disinfection' },
    { value: 'vinyl-flooring', label: 'Vinyl Flooring' },
    { value: 'wooden-flooring', label: 'Wooden Flooring' },
    { value: 'tile-flooring', label: 'Tile Flooring' },
    { value: 'carpet-installation', label: 'Carpet Installation' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'downtown', label: 'Downtown' },
    { value: 'suburbs', label: 'Suburbs' },
    { value: 'north-side', label: 'North Side' },
    { value: 'south-side', label: 'South Side' },
    { value: 'east-side', label: 'East Side' },
    { value: 'west-side', label: 'West Side' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'last-3-months', label: 'Last 3 Months' },
    { value: 'last-6-months', label: 'Last 6 Months' },
    { value: 'this-year', label: 'This Year' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'service-type', label: 'By Service Type' }
  ];

  const handleServiceTypeClick = (serviceType) => {
    onFilterChange('serviceType', serviceType);
  };

  const hasActiveFilters = () => {
    return filters?.serviceType !== 'all' || 
           filters?.location !== 'all' || 
           filters?.dateRange !== 'all' ||
           filters?.sortBy !== 'newest';
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Filter Toggle */}
        <div className="flex items-center justify-between p-4 bg-white border-b border-border">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleFilterPanel}
              iconName="Filter"
              iconPosition="left"
              className={hasActiveFilters() ? 'border-primary text-primary' : ''}
            >
              Filters
              {hasActiveFilters() && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
            <span className="text-sm text-text-secondary">
              {resultCount} projects
            </span>
          </div>
          <Select
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => onFilterChange('sortBy', value)}
            className="w-32"
          />
        </div>
        {/* Mobile Filter Panel */}
        {isFilterPanelOpen && (
          <div className="fixed inset-0 bg-black/50 z-50">
            <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-elevation-5 overflow-y-auto">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-text-primary">Filters</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleFilterPanel}
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>
              </div>
              
              <div className="p-4 space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-primary mb-3">Service Type</h4>
                  <div className="flex flex-wrap gap-2">
                    {serviceTypeOptions?.map((option) => (
                      <button
                        key={option?.value}
                        onClick={() => handleServiceTypeClick(option?.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors duration-200 ${
                          filters?.serviceType === option?.value
                            ? 'bg-primary text-white border-primary' :'bg-white text-text-secondary border-border hover:border-primary hover:text-primary'
                        }`}
                      >
                        {option?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Select
                  label="Location"
                  options={locationOptions}
                  value={filters?.location}
                  onChange={(value) => onFilterChange('location', value)}
                />

                <Select
                  label="Date Range"
                  options={dateRangeOptions}
                  value={filters?.dateRange}
                  onChange={(value) => onFilterChange('dateRange', value)}
                />

                <div className="pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={onClearFilters}
                    disabled={!hasActiveFilters()}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="bg-white border-b border-border sticky top-16 z-30">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
        {/* Service Type Chips */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-text-primary mb-3">Service Type</h3>
          <div className="flex flex-wrap gap-2">
            {serviceTypeOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleServiceTypeClick(option?.value)}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-200 ${
                  filters?.serviceType === option?.value
                    ? 'bg-primary text-white border-primary shadow-elevation-1'
                    : 'bg-white text-text-secondary border-border hover:border-primary hover:text-primary hover:shadow-elevation-1'
                }`}
              >
                {option?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Select
              options={locationOptions}
              value={filters?.location}
              onChange={(value) => onFilterChange('location', value)}
              className="w-40"
            />
            
            <Select
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => onFilterChange('dateRange', value)}
              className="w-40"
            />

            {hasActiveFilters() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                iconName="X"
                iconPosition="left"
                className="text-text-secondary hover:text-primary"
              >
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-text-secondary">
              {resultCount} projects found
            </span>
            <Select
              options={sortOptions}
              value={filters?.sortBy}
              onChange={(value) => onFilterChange('sortBy', value)}
              className="w-40"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;